import { useEffect, useRef, useState } from "react";
import { WAITING_LINES } from "./waiting-copy";
import { FlowisleThinkingGlyph } from "./workflow-icons";
import { GENERATION_STAGES, shellCommandResultSchema, type GenerationProgress, type GenerationStage, type ShellCommandPort } from "@ppt-agent/contracts";

export const STAGE_COPY: Record<GenerationStage, { label: string; phase: number; seconds: number; unit: string; lines: string[] }> = {
  brief: { label: "理解目标 · 任务书", phase: 1, seconds: 50, unit: "份", lines: WAITING_LINES.brief },
  storyline: { label: "编排故事线", phase: 2, seconds: 100, unit: "份", lines: WAITING_LINES.storyline },
  visual_direction: { label: "构思视觉方向", phase: 2, seconds: 50, unit: "份", lines: WAITING_LINES.visual_direction },
  assets: { label: "绘制演示配图", phase: 3, seconds: 80, unit: "张", lines: WAITING_LINES.assets },
  slides: { label: "逐页生成演示", phase: 3, seconds: 80, unit: "页", lines: WAITING_LINES.slides },
  preflight: { label: "检查内容与版式", phase: 3, seconds: 8, unit: "项", lines: WAITING_LINES.preflight },
  compile: { label: "构建可编辑 PPT", phase: 4, seconds: 15, unit: "项", lines: WAITING_LINES.compile },
  verify: { label: "校验交付文件", phase: 4, seconds: 8, unit: "项", lines: WAITING_LINES.verify },
  package: { label: "整理交付文件", phase: 4, seconds: 10, unit: "项", lines: WAITING_LINES.package },
};

/** Explicit estimate between real completed-unit milestones. Never implies model reasoning telemetry. */
export function stagePercent(value: GenerationProgress, now: number): number {
  if (value.status === "completed") return 100;
  const elapsed = Math.max(0, now - Date.parse(value.stageStartedAt)) / 1000;
  const partial = .9 * (1 - Math.exp(-elapsed / STAGE_COPY[value.stage].seconds));
  return Math.min(98, Math.floor(100 * (value.completedUnits + partial) / value.totalUnits));
}

export function useGenerationProgress(port: ShellCommandPort) {
  const [value, setValue] = useState<GenerationProgress>();
  const [disconnected, setDisconnected] = useState(false);
  const [detached, setDetached] = useState<string>();
  const confirmed = useRef(false);
  const [now, setNow] = useState(Date.now());
  const current = useRef<GenerationProgress | undefined>(undefined);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  function accept(next: GenerationProgress) {
    if (!mounted.current || current.current?.operationId !== next.operationId) return;
    if (confirmed.current && Date.parse(next.updatedAt) < Date.parse(current.current.updatedAt)) return;
    if (current.current.status !== "running" && next.status === "running") return;
    current.current = next;
    setValue(next);
  }
  function start(projectId: string, stage: GenerationStage, operationId = globalThis.crypto.randomUUID()) {
    const time = new Date().toISOString();
    const next: GenerationProgress = { projectId, operationId, stage, status: "running", completedUnits: 0, totalUnits: 1,
      completedStages: [], activity: "working", startedAt: time, stageStartedAt: time, updatedAt: time };
    current.current = next; confirmed.current = false; setDetached(undefined); setValue(next); setNow(Date.now()); setDisconnected(false);
    return operationId;
  }
  async function refresh(operationId: string) {
    const active = current.current;
    if (!active || active.operationId !== operationId) return;
    const result = shellCommandResultSchema.parse(await port.execute({ type: "inspect_progress", projectId: active.projectId, operationId }));
    if (result.kind === "progress" && result.value && current.current?.operationId === operationId) { accept(result.value); confirmed.current = true; }
    return result.kind === "progress" ? result.value : undefined;
  }
  async function finish(operationId: string, success: boolean, finalStage?: GenerationStage) {
    let observed: GenerationProgress | null | undefined;
    let readFailed = false;
    try { observed = await refresh(operationId); } catch { readFailed = true; }
    // A lost command connection is not evidence that the server stopped working.
    if (!success && current.current?.operationId === operationId && (observed?.status === "running" || observed?.status === "completed" || (readFailed && confirmed.current && (current.current.status === "running" || current.current.status === "completed")))) {
      setDetached(operationId); setDisconnected(readFailed); return;
    }
    const active = current.current;
    if (!active || active.operationId !== operationId) return;
    if (active.status === "running") {
      const stage = success && finalStage ? finalStage : active.stage;
      accept({ ...active, stage, status: success ? "completed" : "failed",
        completedUnits: success ? active.totalUnits : active.completedUnits,
        completedStages: success ? [...new Set([...active.completedStages, stage])] : active.completedStages,
        updatedAt: new Date(Math.max(Date.now(), Date.parse(active.updatedAt))).toISOString() });
    }
    setDisconnected(false);
  }
  useEffect(() => {
    if (!value || value.status !== "running") return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let failures = 0;
    const operationId = value.operationId;
    async function poll() {
      try {
        await refresh(operationId);
        if (cancelled) return;
        failures = 0; setDisconnected(false);
      } catch { if (cancelled) return; failures++; setDisconnected(true); }
      if (!cancelled) timer = setTimeout(() => void poll(), Math.min(8000, 1000 * 2 ** failures));
    }
    timer = setTimeout(() => void poll(), 200);
    return () => { cancelled = true; clearTimeout(timer); };
    // Only operation identity/status controls this single non-overlapping polling loop.
  }, [port, value?.operationId, value?.status]);
  useEffect(() => {
    if (!value || value.status !== "running" || disconnected) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [value?.operationId, value?.status, disconnected]);
  function reset() { current.current = undefined; confirmed.current = false; setValue(undefined); setDisconnected(false); setDetached(undefined); }
  return { value, now, disconnected, detached, clearDetached: () => setDetached(undefined), start, finish, reset };
}

/** One clock drives both the digits and fill; completion does not block results. */
export function useDisplayedPercent(value: GenerationProgress, now: number, disconnected: boolean) {
  const key = value.operationId + ":" + value.stage;
  const computed = stagePercent(value, value.status === "running" ? now : Date.parse(value.updatedAt));
  const [display, setDisplay] = useState({ key, percent: computed });
  const current = useRef(display);
  const highWater = useRef({ key, percent: computed });
  if (highWater.current.key !== key) highWater.current = { key, percent: 0 };
  highWater.current.percent = Math.max(highWater.current.percent, computed);
  const target = highWater.current.percent;
  useEffect(() => {
    if (current.current.key !== key) {
      current.current = { key, percent: 0 };
      setDisplay(current.current);
    }
    if (disconnected || (value.status !== "running" && value.status !== "completed")) return;
    const from = current.current.percent;
    if (from >= target) return;
    if (globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      current.current = { key, percent: target }; setDisplay(current.current); return;
    }
    const start = Date.now();
    const duration = Math.max(240, Math.min(1800, (target - from) * 24));
    const timer = setInterval(() => {
      const t = Math.min(1, (Date.now() - start) / duration);
      const percent = Math.round(from + (target - from) * t);
      current.current = { key, percent }; setDisplay(current.current);
      if (t === 1) clearInterval(timer);
    }, 24);
    return () => clearInterval(timer);
  }, [key, target, value.status, disconnected]);
  return display.key === key ? display.percent : 0;
}

export function waitingLine(stage: GenerationStage, operationId: string, elapsed: number): string {
  let seed = 2166136261;
  for (const character of operationId + stage) seed = Math.imul(seed ^ character.charCodeAt(0), 16777619);
  const lines = [...STAGE_COPY[stage].lines];
  for (let index = lines.length - 1; index > 0; index--) {
    seed = Math.imul(seed ^ (seed >>> 16), 2246822507);
    const swap = (seed >>> 0) % (index + 1);
    [lines[index], lines[swap]] = [lines[swap]!, lines[index]!];
  }
  return lines[Math.floor(Math.max(0, elapsed) / 12) % lines.length]!;
}

export function GenerationProgressCard({ value, now, disconnected = false }: { value: GenerationProgress; now: number; disconnected?: boolean }) {
  const copy = STAGE_COPY[value.stage];
  const running = value.status === "running";
  const complete = value.status === "completed";
  const elapsed = Math.max(0, Math.floor((now - Date.parse(value.startedAt)) / 1000));

  const percent = useDisplayedPercent(value, now, disconnected);
  const title = complete ? value.stage === "brief" ? "心意已明，静候落笔" : value.stage === "storyline" ? "脉络已成，等你过目" : value.stage === "visual_direction" ? "光色已定，等你点睛" : "这一程灵思，已然成篇" : !running ? "灵思暂歇，静候续篇"
    : waitingLine(value.stage, value.operationId, Math.max(0, (now - Date.parse(value.stageStartedAt)) / 1000));
  const steps = value.stage === "brief" || value.stage === "storyline" || value.stage === "visual_direction"
    ? [value.stage] : GENERATION_STAGES.slice(3);
  return (
    <section className="generation-progress" data-testid="generation-progress" data-stage={value.stage} data-status={value.status} aria-label="AI 生成进度">
      <header className="generation-progress-heading">
        <div>
          <div className="generation-progress-kicker"><FlowisleThinkingGlyph /> FLOWISLE · {complete ? "灵思已成" : running ? "灵思进行时" : "暂待继续"}</div>
          <h2 aria-live="polite">{title.split("，").map((part, index, parts) => <span key={index}>{part}{index < parts.length - 1 ? "，" : ""}</span>)}</h2>
          <p>{copy.label}{running ? value.activity === "retrying" ? " · AI 正在重新推敲" : value.activity === "validating" ? " · 正在核验生成结果" : ["brief", "storyline", "visual_direction", "assets", "slides"].includes(value.stage) ? " · AI 正在构思" : " · 正在处理" : complete ? " · 已完成" : " · 已暂停"}</p>
        </div>
        <strong className="generation-progress-number" aria-hidden="true">{percent}<span>%</span></strong>
      </header>
      <div className="generation-progress-track" role="progressbar" title={complete ? "本阶段已完成" : "阶段估算；图片与页面按实际完成数量更新"} aria-label={copy.label + "进度"} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-valuetext={(complete ? "已完成 " : "预计 ") + percent + "%"}>
        <span style={{ width: percent + "%" }} />
      </div>
      <div className="generation-progress-meta">
        <span>{value.totalUnits > 1 ? "已完成 " + value.completedUnits + " / " + value.totalUnits + " " + copy.unit : ""}</span>
        <span>已用时 {elapsed < 60 ? elapsed + " 秒" : Math.floor(elapsed / 60) + " 分 " + elapsed % 60 + " 秒"}</span>
      </div>
      {steps.length > 1 ? <ol className="generation-progress-steps" aria-label="生成阶段明细">{steps.map((stage) => {
        const done = value.completedStages.includes(stage) && (stage !== value.stage || percent === 100);
        return <li key={stage} data-state={done ? "done" : stage === value.stage ? "current" : "pending"} aria-current={!done && stage === value.stage ? "step" : undefined}><span aria-hidden="true">{done ? "✓" : "·"}</span>{STAGE_COPY[stage].label}<b>{done ? "100%" : stage === value.stage ? percent + "%" : "待开始"}</b></li>;
      })}</ol> : null}
      {disconnected || (!running && !complete) ? <p className="generation-progress-note" role="status">{disconnected ? "连接暂时中断，正在重连。" : value.status === "interrupted" ? "服务已中断，已保留完成的内容。" : "本阶段未完成，内容已保留。"}</p> : null}
    </section>
  );
}
